import { MessageHandlerService } from './../utility/message-handler.service';
import { FaviconService } from './../service/favicon.service';
import { Component, OnInit, OnDestroy, Output, EventEmitter, ViewChild } from '@angular/core';
import { AuthenticationService } from '../authentication/authentication.service';
import { Observable, Subscription } from 'rxjs';
import { UserService } from '../service/user.service';
import { NGXLogger } from 'ngx-logger';
import { User } from 'src/app/user/model/user';
import { AppConfigService } from '../service/app-config.service';
import { TranslateService } from '@ngx-translate/core';
import { MessengerService } from '../service/messenger.service';
import { SettingService } from '../service/setting.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DocumentService } from '../service/document.service';
import { SelectRoleComponent } from 'src/app/shared/select-role/select-role.component';
import { sortBy } from 'lodash';
import { FhirService } from '../service/fhir.service';
import { SelectOrganizationComponent } from 'src/app/shared/select-organization/select-organization.component';

@Component({
  standalone: false,
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy
{
  @ViewChild('selectRole') public selectRole: SelectRoleComponent;
  @ViewChild('selectOrganization') public selectOrganization: SelectOrganizationComponent;

  @Output() public logOutEvent = new EventEmitter<boolean>();

  public channelsUrl = '';
  public channelsAppUrl = '';
  public headerUrl = '';
  public applicationName: SafeHtml = '';
  public loggedInUserFullName = '';
  public loggedInUserRole = '';
  public loggedInUserOrganization = '';
  public isCollapsed = false;
  public isLoggedIn: Observable<boolean>;
  public userImageUrl: string;
  public userImageLoaded = false;
  public hasUserImage = false;
  public headerImageLoaded = false;
  public headerImageUrl: string;
  public hasHeaderImage = false;
  public headerDocumentId = null;
  public userInitials = '';
  public environmentText: string;
  public environmentClass: string;
  public activeRoles = [];
  public activeOrganizations = [];
  public activeOrganizationId = null;
  public roleChangeText = '';
  public organizationChangeText = '';
  public brandDocumentId = null;
  public brandImageLoaded = false;
  public hasBrandImage = false;
  public brandImageUrl: string;

  private authServiceSubscription: Subscription;
  private messagingSubscription: Subscription;

  constructor(
    private authService: AuthenticationService,
    private userService: UserService,
    private logger: NGXLogger,
    private appConfigService: AppConfigService,
    private translate: TranslateService,
    private messengerService: MessengerService,
    private messageHandlerService: MessageHandlerService,
    private settingService: SettingService,
    private faviconService: FaviconService,
    private documentService: DocumentService,
    private fhirService: FhirService,
    public sanitizer: DomSanitizer)
  {
    this.isLoggedIn = authService.isLoggedIn();
    this.authServiceSubscription = this.authService.authenticationChange.subscribe({
      next: (user: User) =>
      {
        if (authService.isAuthenticated)
        {
          this.loggedInUserRole = userService.getRoleText(user.role);
          this.loggedInUserFullName = user && user.name ? user.name : '';
          this.getUserPhotoFromService();
          this.getHeaderPhotoFromService();
          this.getUserRolesAndOrganizations();
        }
      }
    });

    const appConfig = this.appConfigService.getConfig();
    if (appConfig.environmentKey)
    {
      this.environmentClass = `badge-environment-${appConfig.environmentKey.replace(/^ENVIRONMENT-/, "")}`;
      this.translate.get(appConfig.environmentKey).subscribe({
        next: (value) =>
        {
          this.environmentText = value;
        }
      });
    }
    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type)
        {
          switch (message.type)
          {
            case 'userPhotoChanged':
              this.logger.info('HeaderComponent message received: Refreshing user photo');
              this.getUserPhotoFromService();
              break;
            case 'userNameChanged':
              this.logger.info('HeaderComponent message received: Refreshing user First/Last Name');
              this.authService.updateUsername(message.data);  // Save to session storage
              this.loggedInUserFullName = message.data;       // Refresh UI
              this.getUserPhotoFromService();
              break;
            case 'userRolesChanged':
              this.getUserRolesAndOrganizations();
              break;
            case 'headerLogoChanged':
              this.headerDocumentId = parseInt(message.data.headerLogoDocumentId, 10);
              this.getHeaderPhotoFromService();
              break;
            case 'organization-changed':
              this.loggedInUserOrganization = (message.data && message.data.name) ? message.data.name : null;
              break;
          }
        }
      }
    });
  }

  public get fhirEmbedded(): boolean
  {
    return this.fhirService.embedded;
  }

  public get fhirFullSite(): boolean
  {
    return this.fhirService.fullSite;
  }

  public get isPayerRole(): boolean
  {
    return this.authService?.currentUser?.role === 'ROLE_REVENUE_MANAGER_OPERATOR' ||
      this.authService?.currentUser?.role === 'ROLE_REVENUE_MANAGER_ANALYST' ||
      this.authService?.currentUser?.role === 'ROLE_CBO_REVENUE_ANALYST';
  }

  public ngOnInit(): void
  {
    const currentUser = this.authService.currentUser;
    this.loggedInUserFullName = currentUser && currentUser.name ? currentUser.name : '';
    if (currentUser && currentUser.name)
    {
      this.getUserPhotoFromService();
    }

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.channelsUrl = sessionSetting.headerChannelsUrl;
        this.headerUrl = sessionSetting.headerUrl;
        this.channelsAppUrl = '/members-search';
        this.applicationName = this.sanitizer.bypassSecurityTrustHtml(sessionSetting.applicationName);
        this.headerDocumentId = sessionSetting.headerLogoDocumentId;
        this.brandDocumentId = sessionSetting.brandLogoDocumentId;
        this.getHeaderPhotoFromService();
        this.getBrandPhotoFromService();
      }
    });

    this.translate.get(['ROLE-CHANGED', 'ORGANIZATION-CHANGED']).subscribe({
      next: (res) =>
      {
        this.roleChangeText = res['ROLE-CHANGED'];
        this.organizationChangeText = res['ORGANIZATION-CHANGED'];
      }
    });
    this.faviconService.setAppIcon();
    this.getUserRolesAndOrganizations();
  }

  public ngOnDestroy(): void
  {
    if (this.authServiceSubscription !== undefined) { this.authServiceSubscription.unsubscribe(); }
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
    this.userImageLoaded = false;
    this.headerImageLoaded = false;
  }

  public logout(): void
  {
    this.authService.logout();
    this.userImageLoaded = false;
    this.headerImageLoaded = false;
    this.logOutEvent.emit(true);
  }

  public getLoggedInOrganization(): string
  {
    return this.loggedInUserOrganization ? ` - ${this.loggedInUserOrganization}` : '';
  }

  public changeRole(): void
  {
    if (this.authService.isAuthenticated)
    {
      this.selectRole.execute(this.authService.currentUser.userName, this.authService.currentUser.role, this.activeRoles)
        .then((selectedRole) =>
        {
          if (selectedRole && this.authService.currentUser.role !== selectedRole)
          {
            this.authService.changeUserRole(this.authService.currentUser);
            const newRole = this.activeRoles.find(x => x.value === selectedRole);
            this.messageHandlerService.displayDirectMessage('success', this.roleChangeText.replace('{{role}}',
              newRole ? newRole.text : ''));
          }
        });
    }
  }

  public changeOrganization(): void
  {
    if (this.authService.isAuthenticated)
    {
      this.activeOrganizations = [];
      this.activeOrganizationId = null;
      this.userService.getUserById(this.authService.currentUser.id, false).subscribe({
        next: (user: any) =>
        {
          this.activeOrganizations = user.allowedOrganizations;
          this.activeOrganizationId = user.organization ? user.organization.id : null;
          this.selectOrganization.execute(this.authService.currentUser.userName, this.activeOrganizationId, this.activeOrganizations)
            .then((activeOrganization: any) =>
            {
              if (activeOrganization && this.activeOrganizationId != activeOrganization.id)
              {
                this.authService.changeUserRole(this.authService.currentUser);
                this.messageHandlerService.displayDirectMessage('success', this.organizationChangeText.replace('{{name}}', activeOrganization.name));
              }
            });
        }
      });
    }
  }

  private getHeaderPhotoFromService()
  {
    this.headerImageLoaded = false;
    this.hasHeaderImage = false;
    if (this.headerDocumentId !== null && this.headerDocumentId > 0 && this.authService.currentUser)
    {
      this.documentService.retrieveCachedDocument(`/documents/${this.headerDocumentId}/data`).subscribe({
        next: (data) =>
        {
          this.hasHeaderImage = data.content !== null;
          if (this.hasHeaderImage)
          {
            const base64 = data.content;
            this.headerImageUrl = 'data:image/jpeg;base64,' + base64;
          }
          this.headerImageLoaded = true;
        }
      });
    }
  }

  private getUserPhotoFromService()
  {
    this.userImageLoaded = false;
    this.hasUserImage = false;
    if (this.authService.isAuthenticated && this.authService.currentUser)
    {
      this.userService.getCachedPhotoUser(this.authService.currentUser.userName).subscribe({
        next: (data) =>
        {
          this.hasUserImage = data.imageContent !== null;
          if (this.hasUserImage)
          {
            const base64 = data.imageContent;
            this.userImageUrl = 'data:image/jpeg;base64,' + base64;
          }
          else
          {
            const splitted = this.authService.currentUser !== null ? this.authService.currentUser.name.split(' ') : '';
            this.userInitials = (splitted.length >= 2) ? `${splitted[0].charAt(0)}${splitted[1].charAt(0)}` : null;
          }
          this.userImageLoaded = true;
        }
      });
    }
  }

  /**
   * This method set the allowed role for the current logged in user.
   */
  private getUserRolesAndOrganizations(): void
  {
    this.activeRoles = [];
    this.activeOrganizations = [];
    this.activeOrganizationId = null;
    if (this.authService.isAuthenticated)
    {
      this.userService.getUserById(this.authService.currentUser.id, false).subscribe({
        next: (user: any) =>
        {
          this.activeRoles = sortBy(this.userService.roles.filter(r => user.allowedRoles.includes(r.value)), ['text']);
          this.loggedInUserRole = this.userService.getRoleText(user.role);
          this.activeOrganizations = user.allowedOrganizations;
          this.activeOrganizationId = user.organization ? user.organization.id : null;
          this.loggedInUserOrganization = user.organization ? user.organization.name : null;
        }
      });
    }
  }

  private getBrandPhotoFromService()
  {
    this.brandImageLoaded = false;
    this.hasBrandImage = false;
    if (this.brandDocumentId !== null && this.brandDocumentId > 0)
    {
      this.documentService.retrieveBrandImageDocument().subscribe({
        next: (data) =>
        {
          this.hasBrandImage = data.content !== null;
          if (this.hasBrandImage)
          {
            const base64 = data.content;
            this.brandImageUrl = 'data:image/jpeg;base64,' + base64;
          }
          this.brandImageLoaded = true;
        }
      });
    }
    else
    {
      this.brandImageLoaded = true;
    }
  }
}
